import { usePhoneExperience } from "@/lib/phoneExperience";
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Activity, Users, Sun, LogOut, LayoutGrid, X, Scale, CalendarClock, PlugZap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useMobileNav } from "@/context/MobileNavContext";
import { useCompanion } from "@/companion/CompanionContext";
import CompanionPresence from "@/companion/CompanionPresence";
import Avatar from "@/components/Avatar";
import { FRONT_DESK_DESTINATIONS } from "@/lib/frontDeskAccess";

// Boutique color-name mapping. Rainbow leadership sees "Team Rainbow"; each
// boutique swaps in its palette color so PLT staff see "Team Green", etc.
const TEAM_LABELS = {
  PLT: "Team Green",
  FLL: "Team Blue",
  WYN: "Team Pink",
  PNC: "Team Orange",
  RNO: "Team Yellow",
  CC:  "Team Red",
};
const TEAM_LABEL_RAINBOW = "Team Rainbow";

// Sidebar structure follows the rhythm of a manager's day, not software modules:
//   Now  →  Today  →  People  →  Insights  →  Systems
// Two subtle section labels help the eye group without adding visual weight.
const navSections = [
  {
    id: "today",
    label: "Today",
    items: [
      { to: "/",       label: "Home",             icon: Home,   testId: "nav-home"  },
      { to: "/daily",  label: "Today's Journey",  icon: Sun,    testId: "nav-daily" },
    ],
  },
  {
    id: "people",
    label: "People",
    items: [
      { to: "/people", label: "People", icon: Users,    testId: "nav-people" },  // label overridden per boutique below
      { to: "/pulse",  label: "Pulse",  icon: Activity, testId: "nav-pulse"  },
    ],
  },
  {
    id: "work",
    label: "Work",
    items: [
      { to: "/settings/connections/zenoti", label: "Zenoti Connection", icon: PlugZap, testId: "nav-zenoti-connection" },
      // Operations moved out of the shared list because it also needs the isFrontLine
      // gate + isInOperations active-state; still rendered inside this section.
    ],
  },
];

const operationsPath = "/operations";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { open: mobileOpen, setOpen: setMobileOpen } = useMobileNav();
  const companion = useCompanion();

  const phone = usePhoneExperience();
  const isFrontLine = ["front_desk","experience_guide","massage_therapist"].includes(user?.role);

  const isInOperations = location.pathname.startsWith(operationsPath);

  const isCompanionPage = location.pathname.startsWith("/companion") || location.pathname.startsWith("/toto");

  // "People" → boutique-color team label. Rainbow leadership → "Team Rainbow".
  const teamLabel = TEAM_LABELS[user?.boutique_code] || TEAM_LABEL_RAINBOW;

  // Adapt each nav item's label (People swaps to Team Green/Blue/etc), inject
  // "My Schedule" for front-line personas right beneath Home (their personal
  // scheduling doorway — Operations remains a permissioned destination).
  const visibleSections = navSections
    .map((section) => {
      let items = section.items.map((n) => (n.to === "/people" ? { ...n, label: teamLabel } : n));
      if (section.id === "today" && isFrontLine) {
        // Insert My Schedule immediately after Home.
        const homeIdx = items.findIndex((i) => i.to === "/");
        const myDay = {to:"/my-day",label:"My Day",icon:Sun,testId:"nav-my-day"};
        const mySchedule = { to: "/my-schedule", label: "My Schedule", icon: CalendarClock, testId: "nav-my-schedule" };
        items = [...items.slice(0, homeIdx + 1), ...(user?.role === "experience_guide" ? [myDay] : []), mySchedule, ...items.slice(homeIdx + 1)];
      }
      if (isFrontLine) {
        items = items.filter((n) => ["/", "/my-day", "/my-schedule", "/daily", "/people"].includes(n.to));
      }
      if (user?.role === "front_desk") {
        items = section.id === "today" ? FRONT_DESK_DESTINATIONS.map(item => ({ ...item, icon: item.id === "today" ? CalendarClock : Users, testId: `nav-${item.id}` })) : [];
      }
      if (phone) items = items.filter(item => ["/", "/daily", "/my-day", "/my-schedule", "/people", "/profile", "/scheduling/time-off", "/scheduling/preferences", "/help-a-teammate/swaps"].includes(item.to));
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0 || (!phone && section.id === "work")); // Work stays for Operations even if items[] is empty

  // Boutique-aware button palette:
  //   hover  = pale boutique shade (same value used for the companion chat launcher active state)
  //   active = saturated boutique dot color with white text
  // Rainbow leadership (no boutique) keeps the neutral stone treatment.
  const PALETTE = {
    PLT: { bg: "bg-[#F0FDF4]", hover: "hover:bg-[#F0FDF4]", active: "bg-[#22C55E] text-white" },
    FLL: { bg: "bg-[#F0F9FF]", hover: "hover:bg-[#F0F9FF]", active: "bg-[#0EA5E9] text-white" },
    WYN: { bg: "bg-[#FDF2F8]", hover: "hover:bg-[#FDF2F8]", active: "bg-[#EC4899] text-white" },
    PNC: { bg: "bg-[#FFF7ED]", hover: "hover:bg-[#FFF7ED]", active: "bg-[#F97316] text-white" },
    RNO: { bg: "bg-[#FEFCE8]", hover: "hover:bg-[#FEFCE8]", active: "bg-[#C99D36] text-white" },
    CC:  { bg: "bg-[#FEF2F2]", hover: "hover:bg-[#FEF2F2]", active: "bg-[#92262D] text-white" },
  };
  const palette = PALETTE[user?.boutique_code] || { bg: "bg-stone-100", hover: "hover:bg-stone-100", active: "bg-stone-900 text-white" };

  // Collar / tag color is resolved inside the companion's own renderer
  // (e.g. TotoLiquidGlassPresence). Shared shell no longer knows the
  // active companion's presentation details.

  return (
    <>
      {/* Backdrop for mobile drawer */}
      <div
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
        data-testid="sidebar-backdrop"
        className={`lg:hidden fixed inset-0 z-40 bg-stone-900/40 backdrop-blur-sm transition-opacity ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`quiet-glass-nav w-[240px] shrink-0 h-[100dvh] flex flex-col px-6 py-8 z-50 transition-transform lg:transition-none lg:sticky lg:top-0 lg:translate-x-0 fixed left-0 top-0 ${
          mobileOpen ? "translate-x-0 shadow-xl" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        {/* Close button (mobile only) */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          data-testid="sidebar-close"
          className="lg:hidden absolute top-4 right-4 p-1 text-stone-500 hover:text-stone-900"
        >
          <X size={18} strokeWidth={1.5} />
        </button>
      <div className="mb-8">
        {/* The user is the anchor at the top of the sidebar — no brand chrome above them. */}
      </div>

      {/* Logged-in user — the top of the sidebar. The person always sees
          themselves at the top of their day (matches the "Good morning,
          <name>" tone on Home). */}
      {user && (
        <div className="mb-10" data-testid="sidebar-user-top">
          <button
            onClick={() => navigate("/profile")}
            data-testid="sidebar-user"
            className={`flex items-center gap-3 w-full text-left px-2 py-2 rounded-full transition-colors ${palette.hover}`}
          >
            <Avatar name={user.name} picture={user.picture} boutique={user.boutique_code} size={36} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-stone-800 truncate">{user.name}</div>
              <div className="text-xs text-stone-400 truncate">{user.title}</div>
            </div>
          </button>
        </div>
      )}

      <nav className="flex-1 space-y-4" data-testid="sidebar-nav">
        {visibleSections.map((section) => (
          <div key={section.id} className="space-y-1" data-testid={`sidebar-section-${section.id}`}>
            <div className="px-4 pb-1 text-[10px] tracking-[0.12em] text-stone-400 uppercase select-none">
              {section.label}
            </div>

            {section.items.map((item) => {
              // "Today" (/daily) is intentionally boutique-agnostic — no dynamic color.
              const activeCls = item.to === "/daily" ? "bg-stone-900 text-white" : palette.active;
              const hoverCls  = item.to === "/daily" ? "hover:bg-stone-100"      : palette.hover;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm transition-colors ${
                      isActive ? activeCls : `text-stone-500 hover:text-stone-900 ${hoverCls}`
                    }`
                  }
                >
                  <item.icon size={16} strokeWidth={1.25} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {/* Operations lives in the Work section. GMs and above only. */}
            {section.id === "work" && !isFrontLine && !phone && (
              <NavLink
                to="/workspaces"
                data-testid="nav-workspaces"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm transition-colors ${
                    isActive || isInOperations
                      ? `text-stone-900 ${palette.bg}`
                      : `text-stone-500 hover:text-stone-900 ${palette.hover}`
                  }`
                }
              >
                <LayoutGrid size={16} strokeWidth={1.25} />
                <span>Workspace</span>
              </NavLink>
            )}
          </div>
        ))}
      </nav>

      {/* Companion chat launcher — routes through CompanionPresence so the
          shared shell contains no companion-specific assets. Toto's Liquid
          Glass renderer, Morrow's abstract mark, or any future companion
          plugs in behind CompanionPresence without changes here. */}
      {(() => {
        const activeBg = (() => {
          switch (user?.boutique_code) {
            case "PLT": return "var(--plt-bg)";
            case "FLL": return "var(--fll-bg)";
            case "WYN": return "var(--wyn-bg)";
            case "PNC": return "var(--pnc-bg)";
            case "RNO": return "var(--rno-bg)";
            case "CC":  return "var(--cc-bg)";
            default: return "#F5F5F4"; // stone-100 for Rainbow
          }
        })();
        return (
          <button
            type="button"
            onClick={() => navigate("/companion")}
            data-testid="nav-ai"
            aria-label={companion.chatAriaLabel}
            title={companion.chatAriaLabel}
            style={isCompanionPage ? { backgroundColor: activeBg } : undefined}
            className={`group mb-4 mt-2 flex items-center gap-3 pl-3 pr-4 py-2 rounded-2xl transition-colors ${
              isCompanionPage ? "" : "hover:bg-stone-100"
            }`}
          >
            <CompanionPresence size={56} />
            <span
              data-testid="companion-chat-label"
              className={`text-xs tracking-normal ${isCompanionPage ? "text-stone-800" : "text-stone-400 group-hover:text-stone-700"}`}
            >
              {companion.chatCta}
            </span>
          </button>
        );
      })()}

      {user && (
        <div className="pt-6 border-t border-stone-100 space-y-2">
          <button
            onClick={logout}
            data-testid="btn-logout"
            className="flex items-center gap-2 text-xs text-stone-400 hover:text-stone-800 px-2 py-1"
          >
            <LogOut size={12} strokeWidth={1.25} /> Sign out
          </button>
        </div>
      )}
    </aside>
    </>
  );
}
